// src/components/Layout.jsx
import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useMerchantStore } from "../store/useMerchantStore";
import { setMerchantToken } from "../services/api";
import { LanguagePicker } from "./LanguagePicker";
import { t } from "../i18n";
// Grouped like the merchant app's hubs: overview, store, orders, routes.
const MENU_SECTIONS = [
  {
    title: "nav.section.overview",
    items: [{ to: "/", label: "nav.dashboard", icon: "fa-chart-line", end: true }],
  },
  {
    title: "nav.section.store",
    items: [
      { to: "/store-profile", label: "nav.store_profile", icon: "fa-store", end: false },
      { to: "/catalog", label: "nav.catalog", icon: "fa-box-open", end: false },
    ],
  },
  {
    title: "nav.section.orders",
    items: [
      { to: "/orders", label: "nav.orders", icon: "fa-receipt", end: false },
      { to: "/fulfill", label: "nav.fulfill_orders", icon: "fa-clipboard-check", end: false },
      { to: "/ready-orders", label: "nav.ready_orders", icon: "fa-map-location-dot", end: false },
    ],
  },
  {
    title: "nav.section.routes",
    items: [
      { to: "/planned-routes", label: "nav.planned_routes", icon: "fa-route", end: false },
      { to: "/confirmed-routes", label: "nav.confirmed_routes", icon: "fa-circle-check", end: false },
      { to: "/broadcasted-routes", label: "nav.broadcasted_routes", icon: "fa-tower-broadcast", end: false },
    ],
  },
];

/** Browser tab title per page (translation keys; `{id}` is the route number). */
const PAGE_TITLES = [
  [/^\/$/, "nav.dashboard"],
  [/^\/store-profile/, "nav.store_profile"],
  [/^\/catalog/, "nav.catalog"],
  [/^\/orders/, "nav.orders"],
  [/^\/fulfill/, "nav.fulfill_orders"],
  [/^\/ready-orders/, "nav.ready_orders"],
  [/^\/planned-routes/, "nav.planned_routes"],
  [/^\/confirmed-routes/, "nav.confirmed_routes"],
  [/^\/broadcasted-routes/, "nav.broadcasted_routes"],
  [/^\/optimized-route\/(\d+)/, "title.draft_route"],
  [/^\/confirmed-route\/(\d+)/, "title.confirmed_route"],
  [/^\/broadcasted-route\/(\d+)/, "title.live_route"],
];

function pageTitle(pathname) {
  for (const [re, key] of PAGE_TITLES) {
    const m = pathname.match(re);
    if (m) return t(key, { id: m[1] || "" });
  }
  return t("title.not_found");
}

export function Layout() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = `${pageTitle(pathname)} · ${t("title.suffix")}`;
  }, [pathname]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const merchantName = useMerchantStore((s) => s.merchantName);
  const clearSession = useMerchantStore((s) => s.clearSession);

  function logout() {
    setMerchantToken(null);
    clearSession();
    navigate("/login", { replace: true });
  }

  const initial = (merchantName || "M").trim().charAt(0).toUpperCase();

  return (
    <div className="app-layout">
      <aside className={`sidebar ${sidebarCollapsed ? "sidebar--collapsed" : ""}`}>
        <div className="sidebar__header">
          <div>
            <div className="sidebar__logo">{sidebarCollapsed ? "O" : "ODOS"}</div>
            <div className="sidebar__tagline">{t("nav.tagline")}</div>
          </div>
          <button
            type="button"
            className="sidebar__toggle"
            onClick={() => setSidebarCollapsed((c) => !c)}
            aria-label={sidebarCollapsed ? t("nav.expand_menu") : t("nav.collapse_menu")}
          >
            <i className={`fa-solid ${sidebarCollapsed ? "fa-angles-right" : "fa-angles-left"}`} aria-hidden="true" />
          </button>
        </div>
        <nav className="sidebar__nav">
          {MENU_SECTIONS.map((section) => (
            <React.Fragment key={section.title}>
              <div className="sidebar__section">{t(section.title)}</div>
              {section.items.map(({ to, label, icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  title={sidebarCollapsed ? t(label) : undefined}
                  className={({ isActive }) => `sidebar__link ${isActive ? "sidebar__link--active" : ""}`}
                >
                  <i className={`fa-solid ${icon}`} aria-hidden="true" />
                  <span className="sidebar__link-text">{t(label)}</span>
                </NavLink>
              ))}
            </React.Fragment>
          ))}
        </nav>
        <div className="sidebar__footer">
          {merchantName ? (
            <div className="sidebar__merchant" title={merchantName}>
              <div className="sidebar__avatar">{initial}</div>
              {!sidebarCollapsed ? (
                <div style={{ minWidth: 0 }}>
                  <div className="sidebar__merchant-name">{merchantName}</div>
                  <div className="sidebar__merchant-role">{t("nav.merchant_account")}</div>
                </div>
              ) : null}
            </div>
          ) : null}
          {sidebarCollapsed ? null : <LanguagePicker style={{ margin: "0 0 10px", fontSize: 13 }} />}
          <button type="button" className="sidebar__logout" onClick={logout} title={t("nav.log_out")}>
            <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden="true" />
            {sidebarCollapsed ? null : <span>{t("nav.log_out")}</span>}
          </button>
        </div>
      </aside>
      <main className="main-content">
        <div key={pathname} className="page-enter">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
